// Static content used across the site.

export const SECTORS = [
  "Technology and SaaS",
  "Consulting and Professional Services",
  "Media and Entertainment",
  "Financial Services and Fintech",
  "Construction and Engineering",
  "Manufacturing and Industrial",
  "Retail and E commerce",
  "Hospitality and Tourism",
  "Energy and Oil and Gas",
  "Education and EdTech",
  "Healthcare and Biotech",
  "Logistics and Supply Chain",
  "Sustainability and CleanTech",
  "Legal Services",
  "Fashion and Luxury",
  "Sports and Fitness",
  "Food and Agriculture",
];

export const SERVICES = [
  {
    key: "commercial-proof",
    name: "Commercial Proof",
    tagline: "Prove demand before you commit.",
    body: "For companies exploring Saudi, Commercial Proof creates the evidence needed to move with confidence. Each engagement is shaped around the question that matters most: who would buy, why now, what needs to be true, and whether the market is ready for your next move.",
    tiles: [
      { title: "Market Audit", marker: "signal-radar", body: "Understanding the category, demand signals, competitors, and market timing." },
      { title: "Buyer and Stakeholder Mapping", marker: "map-nodes", body: "Identifying who matters across private companies, government linked entities, and strategic accounts." },
      { title: "Government Opportunity Scan", marker: "crosshair", body: "Reviewing visible public sector demand, strategic initiatives, major projects, and opportunity signals relevant to the company’s offer." },
      { title: "Saudi Positioning and Narrative", marker: "narrative-arc", body: "Shaping the message so it makes sense to local buyers and avoids sounding imported." },
      { title: "Target Account List", marker: "target-list", body: "Building a focused list of companies, entities, and decision makers worth pursuing." },
      { title: "Qualified Outreach and Meetings", marker: "dialog-dots", body: "Opening the right conversations and capturing what the market is actually saying." },
      { title: "Go or Pause Recommendation", marker: "fork-arrow", body: "Clarifying whether to move forward, adjust the approach, wait, or pause." },
    ],
  },
  {
    key: "commercial-arm",
    name: "Commercial Arm",
    tagline: "Stay represented while the market develops.",
    body: "For companies developing Saudi opportunities, Commercial Arm gives you a working commercial function in the Kingdom before you commit to a full local team. HuddleUp represents the company on the ground, manages follow ups, supports buyer and partner conversations, tracks market activity, and keeps the Saudi opportunity moving with a clear operating rhythm.",
    tiles: [
      { title: "Local Representation", marker: "pin-location", body: "HuddleUp acts as the company’s commercial presence in the Kingdom, supporting buyer conversations, partner engagement, local meetings, follow ups, and relationship building." },
      { title: "Commercial Rhythm", marker: "rhythm-bars", body: "Creating the weekly or monthly cadence needed to keep Saudi active. This includes priorities, next actions, decision points, pipeline visibility, and clear updates to the client." },
      { title: "Buyer and Partner Follow Through", marker: "follow-through", body: "Making sure conversations do not go cold after the first meeting. HuddleUp supports the rhythm between introductions, proposals, feedback, internal approvals, and next steps." },
      { title: "Pipeline Stewardship", marker: "funnel", body: "Keeping qualified opportunities visible and moving. Tracking target accounts, managing next steps, preparing follow ups, and helping the client understand which accounts deserve focus." },
      { title: "Strategic Opportunity Support", marker: "converging", body: "HuddleUp supports priority opportunities across public sector, government linked, and strategic enterprise accounts. We help shape the commercial narrative, coordinate response inputs, support qualification paths, manage local follow through, and keep the opportunity moving through the right channel." },
      { title: "Market Feedback Loop", marker: "loop-flow", body: "Capturing what the market is actually saying. Buyer objections, pricing reactions, timing issues, procurement signals, partner feedback, and local expectations are turned into clear recommendations." },
      { title: "Strategic Account Support", marker: "ripple", body: "Supporting priority accounts that need more attention. HuddleUp can help prepare for meetings, manage local context, support stakeholder mapping, and keep the account moving between touchpoints." },
      { title: "Partner Channel Support", marker: "branches", body: "Helping the company identify, evaluate, and stay close to potential partners, resellers, introducers, or local ecosystem players without handing over the market too early." },
      { title: "Meeting and Event Presence", marker: "dot-cluster", body: "Representing the company around key meetings, ecosystem events, partner sessions, or market moments where having someone on the ground matters." },
      { title: "Embedded Commercial Support", marker: "integrated-grid", body: "HuddleUp supports the commercial capacity needed to keep the Kingdom active. BD support, local follow ups, account coordination, partner conversations, sales process input, or interim commercial resources while the company is not ready to build a full local team." },
    ],
    outcome: "Commercial Arm keeps the company commercially active in Saudi without hiring a full local team too early. The market stays warm, relationships keep moving, and the client gets a clear view of when to deepen commitment through hiring, setup, corporate services, or a larger Saudi operating layer.",
  },
  {
    key: "commercial-build",
    name: "Commercial Build",
    tagline: "Build a working Saudi presence around real demand.",
    body: "For companies ready to move beyond proof, Commercial Build turns Saudi intent into a working local presence. HuddleUp designs the entry path, manages provider scope and pricing, builds commercial capability, coordinates the operating layer, and keeps the build connected to the commercial reason for entering the Kingdom.",
    tiles: [
      { title: "Entry Architecture", marker: "foundation-layers", body: "Designing the right Saudi path before vendors, documents, and costs start dictating the process. Entity route, activity assumptions, timelines, dependencies, and the commercial logic behind the setup." },
      { title: "Provider Governance", marker: "hierarchy-tree", body: "Managing the people around the process. HuddleUp reviews provider scope, pricing, responsibilities, and timelines so the client keeps visibility and control before committing." },
      { title: "Commercial Capability", marker: "columns-build", body: "Building the first Saudi commercial layer around the opportunity. HuddleUp can support BD strategy, sales process design, first hire planning, candidate profiling, interview support, onboarding structure, and training for Saudi commercial talent." },
      { title: "Operating Infrastructure", marker: "layered-grid", body: "Coordinating the approved providers needed to make the company functional in Saudi — corporate services and compliance, HR, payroll, accounting, office, or other operating needs." },
      { title: "Build Rhythm", marker: "tempo-bars", body: "Keeping the entire process moving with a clear cadence, decision log, timeline, risk view, and next actions across the client, providers, and local stakeholders." },
    ],
  },
];

export const SELECTED_WORK = [
  {
    id: 1,
    product: "Commercial Build",
    title: "Managing the Saudi build for an enterprise software company",
    front: "A US cybersecurity company needed a controlled Saudi build without pulling leadership into every provider, document, and setup decision.",
    problem: "Setup was becoming a management burden.",
    role: "Managed the workstream, challenged provider scope, and helped reduce setup vendor costs by an estimated 30 percent.",
    outcome: "A clearer, more controlled path into the Kingdom.",
  },
  {
    id: 2,
    product: "Commercial Arm",
    title: "Keeping Saudi opportunities active for a global sports media company",
    front: "A UK sports media company needed local follow through while exploring partnerships, brand opportunities, and market entry conversations.",
    problem: "Strong Saudi interest, but no local commercial function.",
    role: "Supported partner conversations, follow ups, opportunity tracking, and local commercial rhythm.",
    outcome: "The market stayed active without hiring a Saudi team too early.",
  },
  {
    id: 3,
    product: "Commercial Proof",
    title: "Testing the Kingdom for a creative production studio",
    front: "A UAE creative production company wanted to know whether Saudi demand justified a deeper market entry push.",
    problem: "The client needed clarity before committing resources.",
    role: "Mapped buyers, reviewed market signals, and scanned public sector opportunity routes.",
    outcome: "The client understood when to move, when to wait, and what to watch next.",
  },
];

export const FAQS = [
  {
    q: "Do we need a Saudi entity before working with HuddleUp?",
    a: "No. HuddleUp can support companies before they decide to set up. In many cases, the right first move is to understand demand, buyer logic, timing, and commercial signal before committing to a local entity.",
  },
  {
    q: "Can HuddleUp help before we know if Saudi makes sense?",
    a: "Yes. That is exactly where Commercial Proof fits. It helps companies test the market, map buyers, understand demand, and decide whether to move forward, adjust, wait, or pause.",
  },
  {
    q: "Do you replace setup providers?",
    a: "No. HuddleUp is not trying to replace every specialist provider. We help clients understand the right path, manage the provider layer, review scope and pricing, and keep setup connected to the commercial reason for entering the Kingdom.",
  },
  {
    q: "Can you support government or strategic opportunities?",
    a: "Yes, but carefully. HuddleUp can help identify opportunity signals, support stakeholder mapping, shape the commercial narrative, coordinate response inputs, and keep the opportunity moving through the right channel. Formal submissions, contracting, licensing, and regulated activities remain subject to the client’s approved structure, providers, and applicable requirements.",
  },
  {
    q: "Can you help us hire or train Saudi commercial talent?",
    a: "Yes. Through Commercial Build, HuddleUp can support first hire planning, candidate profiling, interview structure, onboarding, sales process design, and commercial training for Saudi talent.",
  },
  {
    q: "Which product is right for us?",
    a: "The diagnostic will usually point you toward one of three paths: Commercial Proof if you need to validate demand, Commercial Arm if you need local commercial follow through, or Commercial Build if you are ready to build the operating layer. If there is not enough context, the result will be We need more information.",
  },
  {
    q: "What happens after the diagnostic?",
    a: "You see your recommended path immediately and pick a time on our calendar in the same screen. Your answers travel with the booking so HuddleUp arrives prepared for the 30 minute call.",
  },
  {
    q: "Do you work with early stage companies?",
    a: "Yes, if there is a clear Saudi reason. HuddleUp is most useful when a company has a serious interest in the Kingdom, a relevant offer, and a need to understand whether the market justifies the next move.",
  },
];

export const NOTE_CATEGORIES = [
  "All",
  "Market Signal",
  "Commercial Proof",
  "Commercial Arm",
  "Commercial Build",
  "Strategic Opportunities",
  "Saudi Talent",
  "Provider Layer",
];
